















































import Vue, { PropType } from 'vue';

import { IWorkflowFormFieldNode, WorkflowFormFieldNode } from '@/models';

import {
  ComputationBinaryOperators,
  ComputationNodeType,
  OperatorNodeType
} from '@/models/computation';
import { IFieldErrors } from '@/models/error';

type IOperatorList = {
  icon: string;
  text: string;
  value: ComputationBinaryOperators | null;
}[];

import mixins from '@/utils/mixins';
import { inject as RegistrableInject } from '@/mixins/registrable';
import BindsAttrs from '@/mixins/binds-attrs';

const baseMixins = mixins(
  BindsAttrs,
  RegistrableInject<'formExpression', any>('formExpression')
);
type ErrorBag = Record<number, boolean>;
interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: Vue & {
      validate: () => boolean;
      resetValidation: () => void;
      reset: () => void;
    };
  };
}

export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    name: 'ComputationOperatorNode',

    props: {
      value: {
        required: false,
        type: Object as PropType<IWorkflowFormFieldNode>,
        default: () =>
          new WorkflowFormFieldNode({
            type: ComputationNodeType.Operand,
            subtype: ComputationBinaryOperators.Add
          })
      },

      fieldError: {
        type: Object as PropType<IFieldErrors>,
        default: () => ({})
      },

      disabled: {
        type: Boolean,
        default: false
      },

      isFirst: {
        type: Boolean,
        default: false
      },

      type: {
        type: String as PropType<OperatorNodeType>,
        default: 'binary'
      }
    },

    data() {
      return {
        valid: false,
        internalLazyValue: this.value,
        errorBag: {} as ErrorBag
      };
    },

    computed: {
      hasError(): boolean {
        return !this.valid;
      },

      internalValue: {
        get(): IWorkflowFormFieldNode {
          return this.internalLazyValue;
        },
        set(val: any) {
          if (val === this.internalLazyValue) return;

          if (this.isFirst) {
            if (this.value.isUnaryOperator() !== val.isUnaryOperator()) {
              this.$emit('unary:input', val);
              return;
            }
          }

          this.internalLazyValue = val;

          // hack for passing multiple function on the emit values
          this.$emit('input', val);
        }
      },

      nodeOperatorSubtypeList() {
        const binaryOperatorList: IOperatorList = [
          {
            text: 'Plus',
            icon: 'mdi-plus',
            value: ComputationBinaryOperators.Add
          },
          {
            text: 'Minus',
            icon: 'mdi-minus',
            value: ComputationBinaryOperators.Subtract
          },
          {
            text: 'Multiply',
            icon: 'mdi-close',
            value: ComputationBinaryOperators.Multiply
          },
          {
            text: 'Divide',
            icon: 'mdi-slash-forward',
            value: ComputationBinaryOperators.Divide
          }
        ];

        const bracketOperatorList: IOperatorList = [
          {
            text: 'Start Bracket',
            icon: '',
            value: ComputationBinaryOperators.Start_bracket
          }
        ];

        const operatorSelectList: IOperatorList = [];

        if (this.isFirst) {
          operatorSelectList.push(...bracketOperatorList);
        } else {
          operatorSelectList.push(...binaryOperatorList);
        }
        return operatorSelectList;
      },

      // rules
      operatorRules() {
        return [
          (v: string) => {
            if (this.disabled) return true;
            return v != null || 'An operator is required';
          }
        ];
      }
    },

    watch: {
      value: {
        handler(val) {
          this.internalLazyValue = val;
        },
        deep: true
      }
    },

    methods: {
      setSubtype(subtype?: ComputationBinaryOperators) {
        const newNode = new WorkflowFormFieldNode({
          id: this.internalValue.id,
          type: this.internalValue.type,
          order: this.internalValue.order,
          subtype: subtype
        });
        this.internalValue = newNode;
      },

      /** @public */
      reset() {
        const newNode = new WorkflowFormFieldNode({
          id: this.internalValue.id,
          type: this.internalValue.type,
          order: this.internalValue.order,
          // to clear the subtype will stop form from continuing
          subtype: undefined
        });
        this.internalValue = newNode;
        return this.$refs['form'].reset();
      },

      /** @public */
      resetValidation() {
        return this.$refs['form'].resetValidation();
      },

      validate(): boolean {
        return this.$refs['form'].validate();
      }
    },

    created() {
      this.formExpression && this.formExpression.register(this);
    },

    beforeDestroy() {
      this.formExpression && this.formExpression.unregister(this);
    }
  });
